import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { OverlayModule } from '@angular/cdk/overlay'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Signal,
  ViewEncapsulation,
  computed,
  inject,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'

import { ICellEditorAngularComp } from 'ag-grid-angular'
import { ICellEditorParams } from 'ag-grid-community'
import { IMaskModule } from 'angular-imask'
import { isNil as _isNil } from 'lodash/fp'

import { FlxThemeModule } from '@flowx/angular-theme'
import {
  FlxDatepickerComponent,
  FlxDatepickerModule,
  FlxInputModule,
  FlxSelectModule,
  FlxSwitchModule,
} from '@flowx/angular-ui-toolkit'
import {
  CollectionPrototypeInstanceMeta,
  DataModelType,
  DatepickerTemplateConfig,
  FormFieldTemplateConfig,
  InputTemplateConfig,
  LinkTemplateConfig,
  TableCellTemplateConfig,
  TableTemplateConfig,
  TextTemplateConfig,
  extractPlaceholders,
  getDataModelForProcess,
  getLocalizedDate,
  getMaskSettings,
  getOptionsFromEnumeration,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../../pipes/flx-localize.pipe'
import {
  buildAsyncValidators,
  buildFieldValidators,
  validatorParamChanges$,
} from '../../../utils/validators'
import { getErrorMessages } from '../../helpers'

type CellEditorParams = ICellEditorParams & {
  tableTemplateConfig: TableTemplateConfig
  tableCellTemplateConfig: TableCellTemplateConfig
  firstTemplateConfigWithDynamicText: TextTemplateConfig | LinkTemplateConfig
  dataModelKeyPath: string
}

@Component({
  selector: 'flx-table-editor',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlxThemeModule,
    FlxInputModule,
    FlxDatepickerModule,
    FlxSwitchModule,
    FlxSelectModule,
    IMaskModule,
    OverlayModule,
    FlxLocalizePipe,
  ],
  templateUrl: './flx-table-editor.component.html',
  styleUrl: './flx-table-editor.component.scss',
  host: {
    class: 'flx-table-editor',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTableEditorComponent implements AfterViewInit, ICellEditorAngularComp {
  #injector = inject(Injector)
  #changeDetectorRef = inject(ChangeDetectorRef)
  #destroyRef = inject(DestroyRef)
  #enumOptionsSignal = signal<Array<{ value: unknown; label: string; enabled: boolean }>>([])

  #params = signal<CellEditorParams>(null)

  formControl = new FormControl()

  datepicker = viewChild(FlxDatepickerComponent)
  editorFormControl = viewChild('editorFormControl', { read: ElementRef })

  tableTemplateConfig = signal<TableTemplateConfig>(null)
  tableCellTemplateConfig = signal<TableCellTemplateConfig>(null)
  firstTemplateConfigWithDynamicText = signal<TextTemplateConfig | LinkTemplateConfig>(null)

  isHovered = signal(false)

  formControlTemplateConfig = computed<FormFieldTemplateConfig>(() => {
    const source = this.tableTemplateConfig().displayOptions.flowxProps.source
    const { text } = this.firstTemplateConfigWithDynamicText().displayOptions.flowxProps
    const keyPaths = extractPlaceholders(text)
    const dataModelKeyPath = `${source}.${keyPaths[0]}`

    return {
      ...this.firstTemplateConfigWithDynamicText(),
      key: dataModelKeyPath,
    } as any
  })

  dateDisplayFormatFn = computed(() =>
    getLocalizedDate(this.formControlTemplateConfig() as DatepickerTemplateConfig)
  )
  maskOptions = computed(() =>
    getMaskSettings(this.formControlTemplateConfig() as InputTemplateConfig)
  )

  cellDataType = computed(() => this.#params().column.getColDef().cellDataType as string)

  enumOptions = this.#enumOptionsSignal.asReadonly()

  errorMessages: Record<string, Signal<string>>

  constructor() {
    // needed for error states
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#changeDetectorRef.detectChanges())
  }

  ngAfterViewInit(): void {
    // datepicker does not work properly if the value is set before ngAfterViewInit
    this.formControl.patchValue(this.formControl.value)
  }

  agInit(params: CellEditorParams): void {
    this.#initProperties(params)
  }

  afterGuiAttached(): void {
    if (this.#params().cellStartedEdit) {
      if (this.cellDataType() === 'date') {
        this.datepicker()?.toggle()
        this.editorFormControl()?.nativeElement?.querySelector('input')?.focus()
      } else if (this.cellDataType() !== 'enum') {
        const editorFormControl = this.editorFormControl() as ElementRef
        editorFormControl?.nativeElement?.focus()
      }
    }
  }

  getValue(): any {
    if (this.formControl.valid) {
      return this.#adjustFormValue(this.formControl.value, this.cellDataType())
    }
    return this.#params().value
  }

  getCellStartedEdit(): boolean {
    return this.#params().cellStartedEdit
  }

  getPrototypeInstanceMeta(): CollectionPrototypeInstanceMeta[] | undefined {
    const prototypeInstanceMetaItem = this.#params().data[this.tableCellTemplateConfig().flowxUuid]
    return prototypeInstanceMetaItem ? [prototypeInstanceMetaItem] : undefined
  }

  #initProperties(params: CellEditorParams): void {
    this.#params.set(params)
    this.tableTemplateConfig.set(params.tableTemplateConfig)
    this.tableCellTemplateConfig.set(params.tableCellTemplateConfig)
    this.firstTemplateConfigWithDynamicText.set(params.firstTemplateConfigWithDynamicText)

    this.#initFormValue(params)
    this.formControl.setAsyncValidators(buildAsyncValidators(this.tableCellTemplateConfig()))
    this.formControl.setValidators(buildFieldValidators(this.tableCellTemplateConfig()))
    validatorParamChanges$(this.tableCellTemplateConfig())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.formControl.updateValueAndValidity())

    if (this.cellDataType() === 'enum') {
      this.#initEnumOptions(params)
    }

    runInInjectionContext(this.#injector, () => {
      const prototypeInstanceMeta = this.getPrototypeInstanceMeta()
      this.errorMessages =
        getErrorMessages({
          ...this.tableCellTemplateConfig(),
          prototypeInstanceMeta,
        }) || {}
    })
  }

  #initFormValue(params: CellEditorParams): void {
    const formValue = this.#getInitialFormValue(params)
    this.formControl.setValue(formValue)
  }

  #getInitialFormValue(params: CellEditorParams): any {
    const cellDataType = params.column.getColDef().cellDataType
    return cellDataType === 'boolean'
      ? coerceBooleanProperty(this.#params().value)
      : this.#params().value
  }

  #adjustFormValue(value: any, cellDataType: string): any {
    if (_isNil(value) || value === '') {
      return null
    }
    if (cellDataType === 'date') {
      return new Date(value)
    }
    if (cellDataType === 'number') {
      return parseFloat(value)
    }
    if (cellDataType === 'enum') {
      // Extract value from SelectItem object { value, label, enabled }
      // Handle both SelectItem objects and primitive values for backward compatibility
      return value?.value ?? value
    }
    return value
  }

  #getEnumerationName(dataModelKeyPath: string, processInstanceUuid: string): string {
    if (!dataModelKeyPath) {
      return ''
    }

    const dataModel = getDataModelForProcess(processInstanceUuid)
    const entity = dataModel[dataModelKeyPath]

    if (entity?.type === DataModelType.ENUM && entity?.metadata) {
      const metadata = entity.metadata?.reference
      if (metadata?.name) {
        return metadata.name
      }
    }

    const segments = dataModelKeyPath.split('.')
    return segments.length > 0 ? segments[segments.length - 1] : dataModelKeyPath
  }

  #initEnumOptions(params: CellEditorParams): void {
    const enumerationName = this.#getEnumerationName(
      params.dataModelKeyPath,
      params.tableCellTemplateConfig.processInstanceUuid
    )

    if (!enumerationName) {
      this.#enumOptionsSignal.set([])
      return
    }

    getOptionsFromEnumeration(enumerationName)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((rawOptions) => {
        const mapped = (Array.isArray(rawOptions) ? rawOptions : []).map(
          (opt: { value?: unknown; label?: string; id?: string }) => ({
            value: opt.value ?? opt.id,
            label: typeof opt.label === 'string' ? opt.label : String(opt.value ?? opt.id ?? ''),
            enabled: true,
          })
        )
        this.#enumOptionsSignal.set(mapped)
      })
  }
}
